import React from "react";

// Renders standard soccer pitch markings.
// Canonical coord space:
//   - Full pitch : 680 (width) × 1050 (length). y=0 top goal line, y=1050 bottom goal line.
//   - Half pitch : 680 × 525. HALFWAY LINE at y=0 (top of half pitch) and
//     GOAL LINE at y=525 (bottom of half pitch) — mirroring the standard
//     coaching diagram convention where the pitch is viewed with the goal
//     under attack at the bottom.
// Distances are stored in "canonical units" ≈ decimetres (10 units = 1 metre).
export const PitchMarkings = ({ half = false, stroke = "#FFFFFF" }) => {
  const sw = 3;
  const boxW = 404; // 40.32 m penalty area width
  const boxH = 165; // 16.5 m penalty area depth
  const smallW = 204; // 20.16 m six-yard area width
  const smallH = 55; // 5.5 m six-yard area depth
  const spotOffset = 110; // 11 m penalty spot from goal line
  const arcR = 91.5; // 9.15 m penalty arc radius
  const arcHalf = Math.sqrt(arcR * arcR - (boxH - spotOffset) * (boxH - spotOffset));

  // --- Half pitch: halfway line at y=0 (top), goal line at y=525 (bottom) --
  if (half) {
    const H = 525;
    const boxTop = H - boxH;      // y=360, top edge of penalty area
    const smallTop = H - smallH;  // y=470, top edge of 6-yard box
    const spotY = H - spotOffset; // y=415, penalty spot
    return (
      <g pointerEvents="none">
        {/* Outer boundary */}
        <rect x={0} y={0} width={680} height={H} fill="none" stroke={stroke} strokeWidth={sw} />

        {/* Halfway line (top edge) — centre spot and half circle bulging DOWN into pitch */}
        <circle cx={340} cy={0} r={4} fill={stroke} />
        <path
          d={`M ${340 - arcR} 0 A ${arcR} ${arcR} 0 0 0 ${340 + arcR} 0`}
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
        />

        {/* Penalty area at the bottom (near goal line) */}
        <rect x={(680 - boxW) / 2} y={boxTop} width={boxW} height={boxH} fill="none" stroke={stroke} strokeWidth={sw} />
        <rect x={(680 - smallW) / 2} y={smallTop} width={smallW} height={smallH} fill="none" stroke={stroke} strokeWidth={sw} />
        <circle cx={340} cy={spotY} r={4} fill={stroke} />
        <rect x={300} y={H} width={80} height={5} fill={stroke} />
        {/* D-arc: chord at top of penalty area (y=360), bulges UPWARD into pitch */}
        <path
          d={`M ${340 - arcHalf} ${boxTop} A ${arcR} ${arcR} 0 0 1 ${340 + arcHalf} ${boxTop}`}
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
        />

        {/* Corner arcs at halfway line (top of half pitch) — curve down into pitch */}
        <path d={`M 0 12 A 12 12 0 0 1 12 0`} fill="none" stroke={stroke} strokeWidth={sw} />
        <path d={`M 668 0 A 12 12 0 0 1 680 12`} fill="none" stroke={stroke} strokeWidth={sw} />
        {/* Corner arcs at goal line (bottom) — curve up into pitch */}
        <path d={`M 0 ${H - 12} A 12 12 0 0 0 12 ${H}`} fill="none" stroke={stroke} strokeWidth={sw} />
        <path d={`M 668 ${H} A 12 12 0 0 0 680 ${H - 12}`} fill="none" stroke={stroke} strokeWidth={sw} />
      </g>
    );
  }

  // --- Full pitch: two goals + halfway line + centre circle ----------------
  const L = 1050;
  return (
    <g pointerEvents="none">
      <rect x={0} y={0} width={680} height={L} fill="none" stroke={stroke} strokeWidth={sw} />

      <line x1={0} y1={L / 2} x2={680} y2={L / 2} stroke={stroke} strokeWidth={sw} />
      <circle cx={340} cy={L / 2} r={arcR} fill="none" stroke={stroke} strokeWidth={sw} />
      <circle cx={340} cy={L / 2} r={4} fill={stroke} />

      {/* Top penalty area (goal at y=0) */}
      <rect x={(680 - boxW) / 2} y={0} width={boxW} height={boxH} fill="none" stroke={stroke} strokeWidth={sw} />
      <rect x={(680 - smallW) / 2} y={0} width={smallW} height={smallH} fill="none" stroke={stroke} strokeWidth={sw} />
      <circle cx={340} cy={spotOffset} r={4} fill={stroke} />
      <rect x={300} y={-5} width={80} height={5} fill={stroke} />
      {/* Top D-arc: bulges DOWN into pitch */}
      <path
        d={`M ${340 - arcHalf} ${boxH} A ${arcR} ${arcR} 0 0 0 ${340 + arcHalf} ${boxH}`}
        fill="none"
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* Bottom penalty area (goal at y=1050) */}
      <rect x={(680 - boxW) / 2} y={L - boxH} width={boxW} height={boxH} fill="none" stroke={stroke} strokeWidth={sw} />
      <rect x={(680 - smallW) / 2} y={L - smallH} width={smallW} height={smallH} fill="none" stroke={stroke} strokeWidth={sw} />
      <circle cx={340} cy={L - spotOffset} r={4} fill={stroke} />
      <rect x={300} y={L} width={80} height={5} fill={stroke} />
      {/* Bottom D-arc: bulges UP into pitch (sweep-flag flipped vs top). */}
      <path
        d={`M ${340 - arcHalf} ${L - boxH} A ${arcR} ${arcR} 0 0 1 ${340 + arcHalf} ${L - boxH}`}
        fill="none"
        stroke={stroke}
        strokeWidth={sw}
      />

      <path d="M 0 12 A 12 12 0 0 1 12 0" fill="none" stroke={stroke} strokeWidth={sw} />
      <path d="M 668 0 A 12 12 0 0 1 680 12" fill="none" stroke={stroke} strokeWidth={sw} />
      <path d={`M 0 ${L - 12} A 12 12 0 0 0 12 ${L}`} fill="none" stroke={stroke} strokeWidth={sw} />
      <path d={`M 668 ${L} A 12 12 0 0 0 680 ${L - 12}`} fill="none" stroke={stroke} strokeWidth={sw} />
    </g>
  );
};
